import { CircularProgress, Divider, Grid, InputAdornment } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { AutoComplete, Button, Dialog, Icons, Select, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';

import { removeFieldInspectorApi, saveFieldInspectorApi } from '../../graphql';
import { useMst } from '../../mobx-models/Root';
import {
  IArrayOfAllInspectors,
  IFieldInspectorList,
  IInspectorData,
  IServiceProviderList,
} from '../../models/models';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import { useRxDB } from '../../rxdb/useRxDB';
import useSelectedWpInspectors from '../../rxdb/viewInspector/useSelectedWpInspectors';
import { isJestTest } from '../../test-helpers/isJestTest';
import { mockFIList, mockSPList } from '../../test-helpers/mockData/viewInspectors';
import { useNotification } from '../../utilities/useNotification';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { FieldInspectorType, InspectorType, Key } from '../../utilities/enums';
import { getValueFromLocalStorage } from '../../utilities/utilityFunctions';
import CustomButton from '../CustomButton';
import StyledButton from '../InspectorInfo/StyledButton';
import OfflineDialog from '../OfflineDialog';
import useStyles from './ViewInspectors.styles';

const ToggleButton = styled(MuiToggleButton)(({}) => ({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    color: '#121212',
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
    backgroundColor: 'dimgray',
  },
}));

interface Props {
  openViewInspectors: boolean;
  setOpenViewInspectors: (val: boolean) => void;
  openAssignInspector?: boolean;
  setOpenAssignInspector?: (val: boolean) => void;
  openAddFieldInspector?: boolean;
  workPackageId: number;
  setOpenAddFieldInspector: (val: boolean) => void;
  isViewInspectors?: boolean;
}

const ViewInspectors: React.FC<Props> = ({
  openViewInspectors,
  setOpenViewInspectors,
  openAddFieldInspector = false,
  workPackageId,
  setOpenAddFieldInspector,
  isViewInspectors,
}) => {
  const classes = useStyles();
  const { primaryContext } = useMst();
  const { db } = useRxDB();
  const online = useOnlineStatus();
  const { successNotification, errorNotification } = useNotification();
  const { checkTaskIsThere } = useTaskList();
  const { fieldInspectorsOnWorkPackage, isViewInspectorLoading } =
    useSelectedWpInspectors(workPackageId);

  const [inspectorType, setInspectorType] = useState<string>(InspectorType.Nutrien);
  const [fieldInspectorType, setFieldInspectorType] = useState<string>(FieldInspectorType.FI);
  const [openToggle, setOpentoggle] = useState<boolean>(openAddFieldInspector);
  const [isChanged, setIschanged] = useState<boolean>(false);
  const [inspectorData, setInspectorData] = useState<IInspectorData[]>([]);
  const [arrayOfAllInspectors, setArrayOfAllInspectors] = useState<IArrayOfAllInspectors>({});
  const [spId, setSpId] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [serviceProviderId, setServiceProviderId] = useState<string>();
  const [isOfflineDialogOpen, setIsOfflineDialogOpen] = useState<boolean>(false);
  const [disableToggleButton] = useState(false);
  const [isDECView, setIsDECView] = useState(false);

  const serviceProviderList: IServiceProviderList[] = isJestTest()
    ? mockSPList
    : primaryContext.serviceProviderList.toJSON();
  const fieldInspectorLIst: IFieldInspectorList[] = isJestTest()
    ? mockFIList
    : !isDECView
    ? primaryContext.fieldInspectorList?.toJSON()
    : primaryContext.dataEntryClerksList?.toJSON();

  const handleMainToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      setIschanged(false);
      setFieldInspectorType(inspType);
    }
  };

  useEffect(() => {
    if (!isViewInspectorLoading) {
      setInspectorData(
        fieldInspectorsOnWorkPackage.map(
          (inspector: {
            fieldInspectorId?: number;
            fieldInspectorName: string;
            organizationName?: string;
            clerkId?: number;
            clerkName?: string;
            companyName?: string;
          }) => {
            return {
              pkInspector: inspector.fieldInspectorId,
              fieldInspectorName: inspector.fieldInspectorName,
              organizationName:
                (inspector.organizationName as string) || (inspector.companyName as string),
              isActive: true,
              isChanged: false,
              clerkId: inspector.clerkId,
              clerkName: inspector.clerkName,
            };
          },
        ),
      );
    }
  }, [isViewInspectorLoading]);

  useEffect(() => {
    let isMounted = true;
    const getMasterData = async () => {
      try {
        setIsLoadingData(true);
        if (isMounted) {
          await primaryContext.getFIListFromMobx();
          await primaryContext.getSPListFromMobx();
          await primaryContext.getDataEntryClerksFromMobx();
        }
        setIsLoadingData(false);
      } catch (e) {
        if (isMounted) {
          if (online) errorNotification('data is not loaded');
          setIsLoadingData(false);
        }
      }
    };
    getMasterData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setInspectorType(InspectorType.Nutrien);
  }, []);

  const filteredArrayOfAllInspectors = useMemo(() => {
    return inspectorData?.filter(item => {
      if (fieldInspectorType === FieldInspectorType.DEC) {
        return item.clerkId;
      } else {
        return item.pkInspector;
      }
    });
  }, [inspectorData, fieldInspectorType]);

  const filteredArrayOfActiveInspectors = useMemo(() => {
    return filteredArrayOfAllInspectors.filter(item => item.isActive);
  }, [filteredArrayOfAllInspectors]);

  useEffect(() => {
    const isDECViewSelected = fieldInspectorType === FieldInspectorType.DEC;
    setIsDECView(isDECViewSelected);
  }, [fieldInspectorType]);

  const disableSubmitButton = useMemo(() => {
    return !isChanged && !filteredArrayOfActiveInspectors?.length;
  }, [isChanged, filteredArrayOfActiveInspectors]);

  useEffect(() => {
    const data: IArrayOfAllInspectors = {};
    filteredArrayOfAllInspectors.map((inspector: IInspectorData) => {
      if (inspector.isActive) {
        if (data[inspector?.organizationName]) {
          data[inspector.organizationName].push(inspector);
        } else {
          data[inspector?.organizationName] = [inspector];
        }
      }
    });
    setArrayOfAllInspectors(data);
  }, [filteredArrayOfAllInspectors]);

  const changeState = (index: number, isActive: boolean) => {
    const items: IInspectorData[] = [...inspectorData];
    const item = { ...items[index] };
    item.isActive = isActive;
    item.isChanged = !item.isChanged;
    items[index] = item;
    setInspectorData(items);
  };

  const saveInspectors = async () => {
    setIsLoading(true);
    inspectorData.forEach(
      async (
        inspector: {
          isChanged: boolean;
          isActive: boolean;
          pkInspector?: number;
          clerkId?: number;
        },
        index: number,
      ) => {
        let result;
        try {
          if (!inspector.isChanged) {
            if (inspectorData.length === index + 1) {
              setIsLoading(false);
              setOpenViewInspectors(false);
            }
            return;
          }
          if (inspector.isActive) {
            result = await saveFieldInspectorApi(
              Number(workPackageId),
              Number(getValueFromLocalStorage('inspectorId')),
              inspector.pkInspector,
              inspector.clerkId,
            );
          } else {
            result = await removeFieldInspectorApi(
              Number(workPackageId),
              inspector.pkInspector || 0,
              inspector.clerkId || 0,
            );
          }
          if (!result.errors) {
            successNotification(
              `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} Updated Successfully`,
            );
            setIsLoading(false);
            setOpenViewInspectors(false);
            db?.startReplication();
          } else {
            throw new Error('Error');
          }
        } catch (e) {
          errorNotification('Field Inspector is not saved please try again');
          setIsLoading(false);
        }
      },
    );
  };

  const handleDeleteInspector = (inspId?: number, clerkId?: number) => {
    setIschanged(true);
    const index = inspectorData.findIndex(
      (inspector: { pkInspector?: number; clerkId?: number }) => {
        if (!isDECView) {
          return inspector.pkInspector && inspector.pkInspector === inspId;
        } else {
          return inspector.clerkId === clerkId;
        }
      },
    );
    if (index !== -1) {
      changeState(index, false);
    }
  };

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      if (inspType === InspectorType.Nutrien) {
        setSpId(1);
        setServiceProviderId('Nutrien');
      } else {
        setSpId(0);
      }
      setInspectorType(inspType);
    }
  };

  const check = async (inspectorId: number, clerkId: number) => {
    const data = JSON.parse(
      JSON.stringify(await checkTaskIsThere(Number(workPackageId), inspectorId || 0, clerkId || 0)),
    );
    return openToggle && data.length === 0;
  };

  const handleSearch = (event: React.KeyboardEvent<HTMLDivElement>, value: IFieldInspectorList) => {
    if (event.key === Key.Enter && isChanged) {
      saveInspectors();
      setOpenAddFieldInspector(false);
      return;
    }
    if (value) {
      const index = inspectorData.findIndex(
        (inspector: { pkInspector?: number; clerkId?: number }) => {
          if (!isDECView) {
            return inspector.pkInspector && inspector.pkInspector === value?.pkInspector;
          } else {
            return inspector.clerkId === value?.clerkId;
          }
        },
      );
      if (index === -1) {
        setInspectorData([
          ...inspectorData,
          {
            pkInspector: value.pkInspector as number,
            fieldInspectorName: `${value.firstName} ${value.lastName}`,
            organizationName: value.companyName as string,
            isActive: true,
            isChanged: true,
            clerkId: value.clerkId as number,
          },
        ]);
        setIschanged(true);
      } else {
        changeState(index, true);
      }
    }
  };

  const getDivider = () => {
    return <Divider orientation="horizontal" className={classes.divider} />;
  };

  const displayInspector = (orgName: string, inspectors: IArrayOfAllInspectors) => {
    return (
      <>
        <Grid item container alignItems="center" className={classes.orgNameLabelGrid}>
          <Typography className={classes.nameLabel}>{orgName}</Typography>
        </Grid>
        <Grid item className={classes.locationButtonGrid}>
          {inspectors[orgName].map((inspector: { pkInspector?: number; clerkId?: number }) => {
            return (
              <StyledButton
                inspector={inspector}
                handleDeleteInspector={() =>
                  handleDeleteInspector(inspector.pkInspector, inspector.clerkId)
                }
                canDelete={check(inspector.pkInspector, inspector.clerkId)}
              />
            );
          })}
        </Grid>
        <Grid className={classes.dividerGrid}>{getDivider()}</Grid>
      </>
    );
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isChanged) {
      saveInspectors();
      setOpenAddFieldInspector(false);
    }
  };

  return (
    <>
      <Dialog
        data-testid="view-inspector-dialogue"
        open={openViewInspectors}
        titleText={
          isViewInspectors
            ? `${!isDECView ? 'Field Inspectors' : 'Data Entry Clerks'}`
            : `Add ${!isDECView ? 'Field Inspectors' : 'Data Entry Clerks'}`
        }
        onClose={() => {
          setOpenViewInspectors(false);
          setOpenAddFieldInspector(false);
        }}
        backButton={false}
        dialogActions={
          <CustomButton
            isLoading={isLoading}
            buttonText={isViewInspectors ? 'Update' : 'Add'}
            onClose={() => {
              saveInspectors();
              setOpenAddFieldInspector(false);
            }}
            disabled={disableSubmitButton}
            dataTestId="customButton"
          />
        }
        maxWidth="xs"
        noMinHeight
      >
        {!isLoadingData ? (
          <form onSubmit={handleOnSubmit}>
            <ToggleButtonGroup
              color="#121212"
              value={fieldInspectorType}
              exclusive
              onChange={handleMainToggleChange}
            >
              <ToggleButton
                disabled={disableToggleButton}
                value={FieldInspectorType.FI}
                data-testid="nutrientogglebutton"
              >
                Field Inspector
              </ToggleButton>
              <ToggleButton
                disabled={disableToggleButton}
                value={FieldInspectorType.DEC}
                data-testid="sptogglebutton"
              >
                Data Entry Clerk
              </ToggleButton>
            </ToggleButtonGroup>
            {fieldInspectorType === FieldInspectorType.DEC && disableToggleButton && (
              <Typography
                variant="body2"
                style={{ fontSize: '12px', color: '#93C7EB' }}
                data-testid="dec-info"
              >
                <Icons.InfoFeather className={classes.infoFeather} size="small" strokeWidth="2" />
                <p style={{ float: 'right', marginRight: '22px' }}>
                  Remove the selected Data Entry Clerk(s) to enable in the toggle
                </p>
              </Typography>
            )}
            {getDivider()}
            <Grid>
              {Object.keys(arrayOfAllInspectors)?.length > 0 && (
                <Grid className={classes.outerGridOfDialog}>
                  {Object.keys(arrayOfAllInspectors)
                    ?.filter((orgName: string) => orgName === serviceProviderId)
                    .map((orgName: string) => {
                      return <>{displayInspector(orgName, arrayOfAllInspectors)}</>;
                    })}{' '}
                  {Object.keys(arrayOfAllInspectors)
                    ?.filter((orgName: string) => orgName !== serviceProviderId)
                    .reverse()
                    .map((orgName: string) => {
                      return <>{displayInspector(orgName, arrayOfAllInspectors)}</>;
                    })}
                </Grid>
              )}

              <Grid
                item
                xs={12}
                alignItems="center"
                className={classes.addInspButtonContainer}
                hidden={openToggle}
              >
                <Button
                  color="primary"
                  noMinHeight
                  onClick={() => (online ? setOpentoggle(true) : setIsOfflineDialogOpen(true))}
                  data-testid="addFieldInspectorButton"
                >
                  <Icons.PlusCircleFeather
                    color="primary"
                    className="plusCircle"
                    strokeWidth="1.5px"
                  />
                  <Typography variant="subtitle2">{`Add ${
                    !isDECView ? 'Field Inspector' : 'Data Entry Clerk'
                  }`}</Typography>
                </Button>
              </Grid>

              {openToggle && (
                <>
                  <Grid item xs={12} alignItems="center" className={classes.toggleGrid}>
                    <ToggleButtonGroup
                      color="#121212"
                      value={inspectorType}
                      exclusive
                      onChange={handleToggleChange}
                    >
                      <ToggleButton value={InspectorType.Nutrien}>Nutrien</ToggleButton>
                      <ToggleButton value={InspectorType.Service_Provider}>
                        Service Provider
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {inspectorType === InspectorType.Service_Provider && (
                      <Grid item xs={12} className={classes.spGrid}>
                        <Select
                          className={classes.spSelect}
                          label="Service Provider"
                          list={serviceProviderList.filter(e => e.pkServiceProvider != 1)}
                          getOptionLabel={list => `${list.organizationName}`}
                          getOptionValue={list => list.pkServiceProvider}
                          onChange={event => {
                            setSpId(event.target.value);
                            serviceProviderList
                              .filter(
                                (i: IServiceProviderList) =>
                                  i.pkServiceProvider === event.target.value,
                              )
                              .map((i: IServiceProviderList) =>
                                setServiceProviderId(i.organizationName),
                              );
                          }}
                          isLoading={serviceProviderList.length === 0}
                          defaultValue={spId}
                          data-cy="SP-Type"
                          noMinHeight
                          data-testid="splist"
                        />
                      </Grid>
                    )}
                    <Grid item className={classes.autoCompleteGrid}>
                      <AutoComplete
                        autoSelect={false}
                        noMinHeight
                        size="small"
                        label={`${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'}`}
                        closeIcon=""
                        startAdornment={
                          <InputAdornment position="start">
                            <Icons.SearchFeather
                              color="rgba(149, 149, 149, 1)"
                              className={classes.searchIcon}
                            />
                          </InputAdornment>
                        }
                        endAdornment={
                          <InputAdornment position="end">
                            <Icons.PlusCircleFeather
                              className={classes.plusIcon}
                              color="primary"
                              strokeWidth="1.5px"
                            />
                          </InputAdornment>
                        }
                        placeholder={`Search ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
                        className={classes.autoComplete}
                        onChange={(event, newValue) => {
                          handleSearch(event, newValue);
                        }}
                        list={fieldInspectorLIst
                          .filter(
                            (value: IFieldInspectorList) =>
                              value.fkServiceProvider === spId &&
                              value.isActive &&
                              (value?.firstName?.length !== 0 ||
                                value?.lastName?.length !== 0 ||
                                value?.clerkName?.length !== 0) &&
                              value.pkInspector !==
                                Number(getValueFromLocalStorage('inspectorId')) &&
                              value.clerkId !== Number(getValueFromLocalStorage('clerkId')) &&
                              !inspectorData.find((v: IInspectorData) => {
                                return (
                                  (!isDECView
                                    ? v.pkInspector === value.pkInspector
                                    : v.clerkId === value.clerkId) &&
                                  v.organizationName === value.companyName &&
                                  v.isActive === value.isActive
                                );
                              }),
                          )
                          .sort((a, b) =>
                            a?.firstName?.concat(a.lastName)?.toLowerCase() <
                            b?.firstName?.concat(b.lastName)?.toLowerCase()
                              ? -1
                              : 1,
                          )}
                        value=""
                        getOptionLabel={(option: { firstName: string; lastName: string }) =>
                          option ? `${`${option.firstName} ${option.lastName}`}` : option
                        }
                        data-testid="fieldInspectorId"
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        ) : (
          <CircularProgress className={classes.circularLoader} />
        )}
      </Dialog>
      {isOfflineDialogOpen && (
        <OfflineDialog open={isOfflineDialogOpen} onClose={() => setIsOfflineDialogOpen(false)} />
      )}
    </>
  );
};
export default observer(ViewInspectors);
